import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  ScannerConfig,
  ScannerResult,
  ScannerType,
} from '@/services/scannerService';
import signalRService from '@/services/signalRService';
import StrategySummaryPopover from './StrategySummaryPopover';

// ─── Formatters ────────────────────────────────────────────────────────

const fmtPrice = (v: number) => v.toFixed(v < 1 ? 4 : 2);
const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
const fmtVol = (v: number) => {
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return v.toString();
};
const fmtTime = (s: string) => {
  const d = new Date(s);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'America/New_York' });
};

// ─── Color helpers ─────────────────────────────────────────────────────

const chgColor = (v: number) =>
  v > 0 ? 'text-green-600 dark:text-green-400' : v < 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground';

const chgBg = (v: number) => {
  if (v > 10) return 'bg-green-600/50 dark:bg-green-600/50';
  if (v > 5) return 'bg-green-600/20 dark:bg-green-600/30';
  if (v > 2) return 'bg-green-500/15 dark:bg-green-500/20';
  if (v > 0) return 'bg-green-500/8 dark:bg-green-500/10';
  if (v < -10) return 'bg-red-600/50 dark:bg-red-600/50';
  if (v < -5) return 'bg-red-600/20 dark:bg-red-600/30';
  if (v < -2) return 'bg-red-500/15 dark:bg-red-500/20';
  if (v < 0) return 'bg-red-500/8 dark:bg-red-500/10';
  return '';
};

const haltBg = 'bg-yellow-500/20 dark:bg-yellow-500/30';
const resumeBg = 'bg-cyan-500/15 dark:bg-cyan-500/20';

// ─── Scanner Type → Header color mapping (matches DAS Trader) ──────

type PanelTheme = { headerBg: string; headerText: string; tag: string };

const themeMap: Record<string, PanelTheme> = {
  // Tops / Red-ish headers
  'otc-gappers':          { headerBg: 'bg-red-800', headerText: 'text-white', tag: 'T' },
  'highest-change-open':  { headerBg: 'bg-red-800', headerText: 'text-white', tag: 'T' },
  'premarket-small-up-gap':  { headerBg: 'bg-red-800', headerText: 'text-white', tag: 'T' },
  'premarket-small-down-gap':{ headerBg: 'bg-teal-800', headerText: 'text-white', tag: 'T' },
  'premarket-mid-large-gap': { headerBg: 'bg-teal-800', headerText: 'text-white', tag: 'T' },
  'penny-gainers':        { headerBg: 'bg-red-800', headerText: 'text-white', tag: 'T' },
  'penny-losers':         { headerBg: 'bg-red-800', headerText: 'text-white', tag: 'T' },

  // Momentum / Blue-green headers
  'hod-momo-small':       { headerBg: 'bg-blue-800', headerText: 'text-white', tag: 'M' },
  'hod-momo-large':       { headerBg: 'bg-blue-800', headerText: 'text-white', tag: 'M' },
  'hod-momo-v2-small':    { headerBg: 'bg-green-800', headerText: 'text-white', tag: 'A' },
  'lod-momo-small':       { headerBg: 'bg-purple-800', headerText: 'text-white', tag: 'M' },
  'lod-momo-large':       { headerBg: 'bg-purple-800', headerText: 'text-white', tag: 'M' },

  // Alerts / misc
  'halt-resume':          { headerBg: 'bg-green-800', headerText: 'text-white', tag: 'A' },
  'on-fire':              { headerBg: 'bg-green-800', headerText: 'text-white', tag: 'A' },
  'breakout-volume':      { headerBg: 'bg-green-800', headerText: 'text-white', tag: 'A' },
  'option-volume-plays':  { headerBg: 'bg-blue-800', headerText: 'text-white', tag: 'M' },
};

const getTheme = (id: string): PanelTheme =>
  themeMap[id] ?? { headerBg: 'bg-gray-300 dark:bg-gray-800', headerText: 'text-white', tag: '?' };

// ─── Column definition for each scanner ────────────────────────────────

interface ColDef {
  key: string;
  header: string;
  width: string;         // tailwind width class
  align?: 'left' | 'right';
  render: (r: ScannerResult) => React.ReactNode;
}

// Value cell with conditional coloring
const ValCell: React.FC<{ val: number; fmt?: (v: number) => string; color?: boolean }> = ({
  val, fmt = fmtPrice, color = false,
}) => (
  <span className={color ? chgColor(val) : 'text-foreground/80'}>
    {fmt(val)}
  </span>
);

// Shared column builders
const symCol: ColDef = {
  key: 'symbol', header: 'Symbol', width: 'w-[60px]', align: 'left',
  render: (r) => <span className="font-bold text-foreground">{r.symbol}</span>,
};
const priceCol: ColDef = {
  key: 'price', header: 'Price ($)', width: 'w-[65px]', align: 'right',
  render: (r) => <ValCell val={r.price} color />,
};
const volCol: ColDef = {
  key: 'vol', header: 'Vol Today', width: 'w-[72px]', align: 'right',
  render: (r) => <span className="text-foreground/80">{fmtVol(r.volume)}</span>,
};
const chgPctCol: ColDef = {
  key: 'chgPct', header: 'Chg Close (%)', width: 'w-[82px]', align: 'right',
  render: (r) => <ValCell val={r.changePercent} fmt={fmtPct} color />,
};
const gapCol: ColDef = {
  key: 'gap', header: 'Gap', width: 'w-[55px]', align: 'right',
  render: (r) => <ValCell val={r.gapPercent} fmt={fmtPct} color />,
};
const fltCol: ColDef = {
  key: 'flt', header: 'Flt (Shr)', width: 'w-[68px]', align: 'right',
  render: (r) => <span className="text-muted-foreground">{r.float > 0 ? fmtVol(r.float) : '-'}</span>,
};
const relVolCol: ColDef = {
  key: 'relVol', header: 'Rel Vol', width: 'w-[55px]', align: 'right',
  render: (r) => <span className="text-yellow-600 dark:text-yellow-400">{r.relativeVolume.toFixed(1)}</span>,
};
const timeCol: ColDef = {
  key: 'time', header: 'Time', width: 'w-[72px]', align: 'left',
  render: (r) => <span className="text-muted-foreground tabular-nums">{fmtTime(r.lastUpdate)}</span>,
};
const chg2MinCol: ColDef = {
  key: 'chg2m', header: '2Min%', width: 'w-[60px]', align: 'right',
  render: (r) => <ValCell val={r.changePercent2Min ?? r.changePercent} fmt={fmtPct} color />,
};
const vwapCol: ColDef = {
  key: 'vwap', header: 'VWAP', width: 'w-[60px]', align: 'right',
  render: (r) => <span className="text-foreground/80">{fmtPrice(r.vwap)}</span>,
};
const stratCol: ColDef = {
  key: 'strat', header: 'Strategy', width: 'w-[95px]', align: 'left',
  render: () => <span className="text-cyan-600 dark:text-cyan-300 text-[10px]">Turbo Breaks Up</span>,
};

// Per-scanner column sets
const colSets: Record<string, ColDef[]> = {
  'otc-gappers':          [symCol, priceCol, gapCol, chgPctCol, volCol, fltCol, relVolCol],
  'highest-change-open':  [timeCol, symCol, priceCol, chgPctCol, volCol, vwapCol, relVolCol],
  'halt-resume':          [timeCol, 
    { key: 'type', header: 'Type', width: 'w-[55px]', align: 'left',
      render: (r) => <span className={r.isHalted ? 'text-red-400 font-bold' : 'text-green-400 font-bold'}>{r.isHalted ? 'HALT' : 'RESUME'}</span> },
    symCol, 
    { key: 'desc', header: 'Description', width: 'w-[120px]', align: 'left',
      render: (r) => <span className="text-foreground/80 truncate">{r.haltReason ?? (r.isHalted ? 'Halted' : 'Resumed')}</span> }],
  'premarket-small-up-gap':  [symCol, priceCol, gapCol, volCol, fltCol, chgPctCol],
  'premarket-small-down-gap':[symCol, priceCol, gapCol, volCol, fltCol, chgPctCol],
  'premarket-mid-large-gap': [symCol, priceCol, gapCol, volCol, fltCol, chgPctCol],
  'hod-momo-small':       [timeCol, symCol, priceCol, chgPctCol, volCol, relVolCol, fltCol, stratCol],
  'hod-momo-large':       [timeCol, symCol, priceCol, chgPctCol, volCol, relVolCol, fltCol, stratCol],
  'hod-momo-v2-small':    [timeCol, symCol, 
    { key: 'desc2', header: 'Description', width: 'w-[180px]', align: 'left',
      render: (r) => <span className="text-foreground/80 truncate text-[10px]">New High 0.003. Next resistance {fmtPrice(r.high)}</span> }],
  'lod-momo-small':       [timeCol, symCol, priceCol, chgPctCol, volCol, relVolCol, fltCol, stratCol],
  'lod-momo-large':       [timeCol, symCol, priceCol, chgPctCol, volCol, relVolCol, fltCol, stratCol],
  'penny-gainers':        [symCol, priceCol, chgPctCol, volCol, fltCol, relVolCol],
  'penny-losers':         [symCol, priceCol, chgPctCol, volCol, fltCol, relVolCol],
  'on-fire':              [timeCol, symCol, priceCol, 
    { key: 'typeFire', header: 'Type', width: 'w-[30px]', align: 'left',
      render: (r) => <span className={`font-bold ${r.changePercent > 0 ? 'text-green-400' : 'text-red-400'}`}>{r.changePercent > 0 ? '↑$' : '↓$'}</span> },
    relVolCol, chg2MinCol],
  'breakout-volume':      [timeCol, symCol, priceCol, chgPctCol, volCol, relVolCol],
  'option-volume-plays':  [timeCol, symCol, 
    { key: 'strategy2', header: 'Strategy', width: 'w-[130px]', align: 'left',
      render: () => <span className="text-cyan-600 dark:text-cyan-300 text-[10px]">Moving Loving with Options Volume</span> },
    priceCol, relVolCol],
};

const defaultCols: ColDef[] = [symCol, priceCol, chgPctCol, volCol, relVolCol];

// ─── Mini Panel Component ──────────────────────────────────────────────

interface MiniPanelProps {
  scannerId: string;
  title: string;
  results: ScannerResult[];
  onSymbolClick?: (symbol: string) => void;
  className?: string;
}

const MiniPanel: React.FC<MiniPanelProps> = ({ scannerId, title, results, onSymbolClick, className = '' }) => {
  const theme = getTheme(scannerId);
  const cols = colSets[scannerId] ?? defaultCols;

  return (
    <div className={`flex flex-col border border-border overflow-x-auto overflow-y-hidden scrollbar-thin ${className}`}>
      {/* Header bar */}
      <div className={`flex items-center gap-1.5 px-2 py-0.5 ${theme.headerBg} ${theme.headerText} select-none`}>
        <span className="text-[10px] font-bold border border-white/40 px-1 rounded leading-tight">
          {theme.tag}
        </span>
        <span className="text-[11px] font-semibold truncate flex-1">{title}</span>
        <span className="text-[10px] text-white/60 tabular-nums">{results.length}</span>
        <StrategySummaryPopover scannerId={scannerId} compact />
      </div>

      {/* Column headers */}
      <div className="flex gap-0 px-1 py-0.5 bg-muted border-b border-border text-[10px] font-medium text-muted-foreground select-none">
        {cols.map((c) => (
          <div key={c.key} className={`${c.width} shrink-0 ${c.align === 'right' ? 'text-right' : 'text-left'} px-0.5`}>
            {c.header}
          </div>
        ))}
      </div>

      {/* Rows */}
      <div className="flex-1 overflow-y-auto bg-background min-h-0 scrollbar-thin">
        {results.length === 0 ? (
          <div className="text-[10px] text-muted-foreground text-center py-4">Waiting for data…</div>
        ) : (
          results.map((r, i) => {
            const rowBg = r.isHalted
              ? haltBg
              : r.resTime
                ? resumeBg
                : chgBg(r.changePercent);

            return (
              <div
                key={`${r.symbol}-${i}`}
                onClick={() => onSymbolClick?.(r.symbol)}
                className={`flex gap-0 px-1 py-[2px] text-[11px] cursor-pointer hover:bg-foreground/10 transition-colors ${rowBg} ${
                  i % 2 === 0 ? '' : 'bg-muted/40'
                }`}
              >
                {cols.map((c) => (
                  <div key={c.key} className={`${c.width} shrink-0 ${c.align === 'right' ? 'text-right' : 'text-left'} px-0.5 truncate`}>
                    {c.render(r)}
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Dashboard Config: 5 rows matching the screenshot ──────────────────

interface PanelConfig {
  id: string;
  name: string;
  type: ScannerType;
}

// Row 1: OTC Gappers | Highest Change from Open | (see before news placeholder) | Halt/Resume
const row1: PanelConfig[] = [
  { id: 'otc-gappers', name: 'OTC Gappers', type: 'OtcGappers' },
  { id: 'highest-change-open', name: 'Highest Change from Open', type: 'HighestChangeFromOpen' },
  { id: 'halt-resume', name: 'Halt / Resume', type: 'HaltResume' },
];

// Row 2: Pre-Market Small Up Gappers | HOD Momo Small | HOD Momo Large | HOD Momo v2 Small
const row2: PanelConfig[] = [
  { id: 'premarket-small-up-gap', name: '(Pre-Market) Small Up Gappers', type: 'PreMarketSmallUpGap' },
  { id: 'hod-momo-small', name: 'HOD Momentum Scanner (Small Caps)', type: 'HodMomoSmallCap' },
  { id: 'hod-momo-large', name: 'HOD Momentum Scanner (Large Caps)', type: 'HodMomoLargeCap' },
  { id: 'hod-momo-v2-small', name: 'HOD Momentum v2 (Small Caps)', type: 'HodMomoV2SmallCap' },
];

// Row 3: Penny Gainers | (continuation — filled by row 2 spanning) | On Fire
const row3: PanelConfig[] = [
  { id: 'penny-gainers', name: 'Penny Stock Gainers Today', type: 'PennyGainers' },
  { id: 'on-fire', name: 'On Fire', type: 'OnFire' },
  { id: 'breakout-volume', name: 'Breaking out on Volume', type: 'BreakoutVolume' },
];

// Row 4: LOD Momo Small | LOD Momo Large
const row4: PanelConfig[] = [
  { id: 'lod-momo-small', name: 'LOD Momentum Scanner (Small Caps)', type: 'LodMomoSmallCap' },
  { id: 'lod-momo-large', name: 'LOD Momentum Scanner (Large Caps)', type: 'LodMomoLargeCap' },
];

// Row 5: Penny Losers | Pre-Market Small Down | Pre-Market Mid-Large | Option Vol Plays
const row5: PanelConfig[] = [
  { id: 'penny-losers', name: 'Penny Stock Losers Today', type: 'PennyLosers' },
  { id: 'premarket-small-down-gap', name: '(Pre-Market) Small Down Gappers', type: 'PreMarketSmallDownGap' },
  { id: 'premarket-mid-large-gap', name: '(Pre-Market) Mid-Large Gappers', type: 'PreMarketMidLargeGap' },
  { id: 'option-volume-plays', name: 'Option Volume Plays 300%, 200% & 150%', type: 'OptionVolumePlays' },
];

const allPanels: PanelConfig[] = [...row1, ...row2, ...row3, ...row4, ...row5];

// ─── Main Component ────────────────────────────────────────────────────

export const ProScannerDashboard: React.FC<{
  onSymbolSelect?: (symbol: string) => void;
}> = ({ onSymbolSelect }) => {
  const [scannerResults, setScannerResults] = useState<Record<string, ScannerResult[]>>({});
  const [connected, setConnected] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // On phones the multi-column DAS-style grid is unreadable — collapse to 1 column.
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Subscribe to all pro scanner grains via SignalR
  useEffect(() => {
    let mounted = true;

    const setup = async () => {
      signalRService.addScannerUpdateListener('pro-scanner-dashboard', (scannerId: string, update: any) => {
        if (!mounted) return;
        const results: ScannerResult[] = update.results ?? [];
        setScannerResults((prev) => ({ ...prev, [scannerId]: results }));
      });

      for (const panel of allPanels) {
        await signalRService.subscribeToScanner(panel.id);
      }
      if (mounted) setConnected(true);
    };

    setup().catch((err) => console.error('Pro scanner setup error:', err));

    const timer = setInterval(() => setCurrentTime(new Date()), 1000);

    return () => {
      mounted = false;
      clearInterval(timer);
      signalRService.removeScannerUpdateListener('pro-scanner-dashboard');
      allPanels.forEach((p) => signalRService.unsubscribeFromScanner(p.id));
    };
  }, []);

  const handleSymbolClick = useCallback(
    (symbol: string) => onSymbolSelect?.(symbol),
    [onSymbolSelect],
  );

  const getResults = (id: string) => scannerResults[id] ?? [];

  const renderRow = (panels: PanelConfig[], rowHeight: string) => (
    <div className={`grid gap-[2px] ${rowHeight}`} style={{ gridTemplateColumns: isNarrow ? '1fr' : `repeat(${panels.length}, 1fr)`, gridAutoRows: isNarrow ? '240px' : undefined }}>
      {panels.map((p) => (
        <MiniPanel
          key={p.id}
          scannerId={p.id}
          title={p.name}
          results={getResults(p.id)}
          onSymbolClick={handleSymbolClick}
        />
      ))}
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-background text-foreground overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-1 bg-card border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold tracking-wide text-foreground">Pro Scanner</span>
          <Badge
            variant={connected ? 'default' : 'secondary'}
            className={`text-[10px] ${connected ? 'bg-green-600 text-white' : ''}`}
          >
            {connected ? 'LIVE' : 'CONNECTING…'}
          </Badge>
          <span className="text-[10px] text-muted-foreground">{allPanels.length} panels</span>
        </div>
        <div className="font-mono font-bold text-sm text-green-600 dark:text-green-400 tabular-nums">
          {currentTime.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone: 'America/New_York',
          })}
          <span className="text-[10px] text-muted-foreground ml-1">ET</span>
        </div>
      </div>

      {/* Grid of scanner panels — 5 rows */}
      <div className="flex-1 flex flex-col gap-[2px] p-[2px] min-h-0 overflow-auto">
        {renderRow(row1, 'min-h-[160px]')}
        {renderRow(row2, 'min-h-[175px]')}
        {renderRow(row3, 'min-h-[160px]')}
        {renderRow(row4, 'min-h-[140px]')}
        {renderRow(row5, 'min-h-[160px]')}
      </div>
    </div>
  );
};

export default ProScannerDashboard;
